import { getProfilePageUrl } from 'constants/pages';
import type { VerificationLevel } from 'types/verification';
import { ButtonLink } from 'components/Button';
import VerificationBadge from 'components/VerificationBadge';
import classNames from 'styles/utils/classNames';

interface Props {
  name: string;
  /** Omit when the user never chose one — they carry a UUID placeholder. */
  username?: string;
  /** Overrides the `username`-derived profile path, for a hidden placeholder handle. */
  profileHref?: string;
  imageUrl: string;
  verificationLevel?: VerificationLevel;
  /** `lg`: Figma "Modal M" from lg up — 64px avatar, button beside the name. */
  sizeDesktop?: 'lg';
}

/** Avatar + name + handle with a "View profile" button — the Organizer/Player row. */
export default function PersonCard({
  name,
  username,
  profileHref,
  imageUrl,
  verificationLevel,
  sizeDesktop,
}: Props) {
  const isLargeAtDesktop = sizeDesktop === 'lg';
  const href = profileHref || (username ? getProfilePageUrl(username) : '');

  return (
    <div
      className={classNames(
        'flex items-center justify-between gap-ds-sm',
        isLargeAtDesktop && 'lg:justify-start lg:gap-ds-2xl',
      )}
    >
      <div
        className={classNames(
          'flex min-w-0 items-center gap-ds-sm',
          isLargeAtDesktop && 'lg:gap-ds-md',
        )}
      >
        <div
          className={classNames(
            'relative h-10 w-10 shrink-0',
            isLargeAtDesktop && 'lg:h-16 lg:w-16',
          )}
        >
          <img src={imageUrl} alt={name} className="h-full w-full rounded-full object-cover" />

          <VerificationBadge
            level={verificationLevel}
            className={classNames('size-4', isLargeAtDesktop && 'lg:size-5')}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-ds-xs">
          <span
            className={classNames(
              'typography-product-card-title-mobile truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
              isLargeAtDesktop && 'lg:typography-product-card-title-desktop',
            )}
          >
            {name}
          </span>
          {username && (
            <span className="typography-product-text-card-mobile truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              @{username}
            </span>
          )}
        </div>
      </div>

      {!!href && (
        <ButtonLink href={href} variant="secondary" size="sm" isInline>
          View profile
        </ButtonLink>
      )}
    </div>
  );
}
